import { TRANSLATION_KEYS } from '@/app/app-translation-keys';
import { Injectable } from '@angular/core';
import { ToastMessage, ToastOptions } from '@core/models/toast.model';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts$ = new BehaviorSubject<ToastMessage[]>([]);
  public toasts = this.toasts$.asObservable();

  private defaultIcons = {
    success: 'bi bi-check-circle-fill',
    danger: 'bi bi-x-circle-fill',
    warning: 'bi bi-exclamation-triangle-fill',
    info: 'bi bi-info-circle-fill'
  };

  private defaultSettings = {
    delay: 5000,
    autohide: true
  };

  constructor(private translate: TranslateService) {}

  private showToast(
    type: 'success' | 'danger' | 'warning' | 'info',
    message: string,
    options: ToastOptions = {}
  ): string {
    const id = this.generateId();
    const toast: ToastMessage = {
      id,
      type,
      message,
      title: options.title || this.getDefaultTitle(type),
      delay: options.delay ?? this.defaultSettings.delay,
      autohide: options.autohide ?? this.defaultSettings.autohide,
      icon: options.icon || this.defaultIcons[type],
      timestamp: new Date()
    };

    // 🔥 Chỉ giữ lại 1 toast duy nhất (replace mode)
    this.toasts$.next([toast]);

    return id;
  }

  success(message: string, options: ToastOptions = {}): string {
    return this.showToast('success', message, options);
  }

  error(message: string, options: ToastOptions = {}): string {
    return this.showToast('danger', message, options);
  }

  warning(message: string, options: ToastOptions = {}): string {
    return this.showToast('warning', message, options);
  }

  info(message: string, options: ToastOptions = {}): string {
    return this.showToast('info', message, options);
  }

  remove(id: string): void {
    this.toasts$.next([]);
  }

  clear(): void {
    this.toasts$.next([]);
  }

  getCount(): number {
    return this.toasts$.value.length;
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  private getDefaultTitle(type: string): string {
    const titles = {
      success: TRANSLATION_KEYS.TOAST.SUCCESS.key,
      danger: TRANSLATION_KEYS.TOAST.ERROR.key,
      warning: TRANSLATION_KEYS.TOAST.ERROR.key,
      info: TRANSLATION_KEYS.TOAST.ERROR.key,
    };
    const key = titles[type as keyof typeof titles] || '';

    return this.translate.instant(key);
  }

  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = Math.floor((now.getTime() - timestamp.getTime()) / 1000);

    if (diff < 60) return 'Vừa xong';
    if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
    return `${Math.floor(diff / 86400)} ngày trước`;
  }
}
